<template>
    <div class="rounded border-gray-300 bg-white py-4 shadow">
        <div
            class="flex flex-wrap justify-start gap-2 border-b border-primary-350 px-4 pb-4 text-[16px] text-gray-600 text-primary-525"
        >
            <div class="">#{{ questionNumber }}</div>
            <div
                v-if="question.focus_area && quizType !== 'focus_area'"
                class=""
            >
                {{ question.focus_area.title }}
            </div>
            <div
                v-if="
                    question.focus_area &&
                    quizType !== 'focus_area' &&
                    question.category
                "
            >
                &raquo;
            </div>
            <div v-if="question.category" class="">
                {{ question.category.title }}
            </div>
            <div v-if="question.category && question.topic" class="">
                &raquo;
            </div>
            <div v-if="question.topic" class="">
                {{ question.topic.title }}
            </div>
        </div>

        <div class="mx-4 w-auto py-4 text-[16px] font-light leading-relaxed">
            <div
                v-if="question.is_tei"
                class="mb-4 flex items-center justify-start"
            >
                <div
                    title="Technology Enhanced Item"
                    class="w-auto rounded border border-green-500 px-2 py-1 text-[16px] text-green-500"
                >
                    Technology Enhanced Item
                </div>
            </div>
            <span v-html="question.content"></span>
        </div>

        <div class="mx-4 pb-4">
            <div
                v-if="question.student_response.c"
                class="text-[16px] font-semibold text-green-500"
            >
                Correct!
            </div>
            <div v-else class="text-[16px] font-semibold text-red-400">
                Incorrect
            </div>
        </div>
        <div class="mx-4 mb-4 flex flex-col gap-2">
            <div :class="getAnswerCss(answer)" v-for="answer in orderedAnswers">
                <div v-if="getAnswerWasSelected(answer)" class="text-gray-425">
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <rect
                            width="20"
                            height="20"
                            rx="4"
                            fill="currentColor"
                        />
                        <path
                            d="M15.3307 6L7.9974 13.3333L4.66406 10"
                            stroke="white"
                            stroke-width="1.33333"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </div>
                <div v-else class="">
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <rect
                            x="0.5"
                            y="0.5"
                            width="19"
                            height="19"
                            rx="3.5"
                            fill="white"
                        />
                        <rect
                            x="0.5"
                            y="0.5"
                            width="19"
                            height="19"
                            rx="3.5"
                            stroke="#E4E6EA"
                        />
                    </svg>
                </div>
                <div class="">
                    {{ answer.text }}
                </div>
                <div
                    v-if="getAnswerWasSelected(answer) && answer.is_correct"
                    class="ml-auto flex"
                >
                    <div>
                        <svg
                            width="18"
                            height="13"
                            viewBox="0 0 18 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M17 1L6 12L1 7"
                                stroke="#1F9294"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </div>
                </div>
                <div
                    v-if="getAnswerWasSelected(answer) && !answer.is_correct"
                    class="ml-auto flex text-red-500"
                >
                    <div class="">Incorrect</div>
                    <div class="">
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M18 6L6 18"
                                stroke="#F9414C"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M6 6L18 18"
                                stroke="#F9414C"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </div>
                </div>
                <div
                    v-if="!getAnswerWasSelected(answer) && answer.is_correct"
                    class="ml-auto flex text-red-500"
                >
                    <div class="">Missed</div>
                    <div class="">
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M18 6L6 18"
                                stroke="#F9414C"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M6 6L18 18"
                                stroke="#F9414C"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="question.answer_rationale" class="mx-4 bg-gray-300 p-4">
            <div
                :class="
                    'flex flex-col items-start gap-4' +
                    ' ' +
                    (question.video_answer_rationale
                        ? 'lg:grid lg:grid-cols-2 '
                        : 'lg:flex-row')
                "
            >
                <div class="">
                    <div class="mb-2 text-[16px] font-semibold">
                        Explanation
                    </div>
                    <div class="text-[16px] leading-loose text-gray-700">
                        {{ question.answer_rationale }}
                    </div>
                </div>
                <div
                    class="aspect-video w-full lg:aspect-auto lg:h-auto lg:w-auto"
                    v-if="question.video_answer_rationale"
                >
                    <video-player
                        :video-token="question.video_answer_rationale"
                    ></video-player>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import VideoPlayer from "@/Components/VideoPlayer.vue";

export default {
    name: "StudentQuizReviewQuestion",
    components: { VideoPlayer },
    props: {
        question: {
            type: Object,
            default: {},
        },
        quizType: {
            type: String,
            default: "",
        },
        questionNumber: {
            type: Number,
            default: 1,
        },
    },
    methods: {
        getAnswerCss(answer) {
            let answerCss =
                "gap-4 items-center rounded flex border border-gray-200 p-4 text-[16px] font-light";

            if (this.getAnswerWasSelected(answer)) {
                if (answer.is_correct) {
                    answerCss += " " + "border-green-400";
                } else {
                    answerCss += " " + "border-red-500";
                }
            }

            return answerCss;
        },
        getAnswerWasSelected(answer) {
            return _.includes(this.question.student_response.ans, answer.id);
        },
    },
    computed: {
        orderedAnswers() {
            let vm = this;
            let orderedAnswers = [];

            this.question.student_response.oo.forEach(function (optionIndex) {
                orderedAnswers.push(vm.question.answers[parseInt(optionIndex)]);
            });

            return orderedAnswers;
        },
    },
    mounted() {
        this.question.student_response.ans =
            this.question.student_response.ans.map(function (answer) {
                return parseInt(answer);
            });
    },
};
</script>
